import { useEffect, useState } from "react";
import styled from "styled-components";

import Select from "components/atoms/input/Select";
import InputText from "components/atoms/input/Text";
import ModalStyle from "components/atoms/modal";
import { Caption, H2, TextSm } from "components/atoms/text";
import FormAddress from "components/organisms/FormAddress";
import Btn from "components/atoms/buttons";
import COLORS from "components/atoms/colors";
import FormUser from "components/layouts/Header/ModalMy/User";
import FormCompany from "components/layouts/Header/ModalMy/Company";
import ModalDeleteMe from "components/layouts/Header/ModalMy/ModalDeleteMe";
import Modal from "components/organisms/Modal";

import { InputForm } from "types/Form";
import Address from "types/Address";

import useJob from "apis/queries/useJob";
import useUserMe from "apis/queries/useUserMe";
import useCompanyMe from "apis/queries/useCompanyMe";
import useEditUserMe from "apis/mutations/user/useEditUserMe";
import useEditCompanyMe from "apis/mutations/company/useEditCompanyMe";

import useInput from "hooks/useInput";
import getParsedJSON from "utils/getParsedJSON";

interface Props {
  onClose: () => void;
}

// 마이페이지 모달
const ModalMy = ({ onClose }: Props) => {
  const [selectIndex, setSelectIndex] = useState(0);
  const [jobEtc, setJobEtc] = useState("");
  const [errorMessage, setErrorMessage] = useState<
    string | string[] | undefined
  >("");

  // 내정보 수정 api - 일반회원
  const editUserMe = useEditUserMe({
    onError: (message) => setErrorMessage(message),
    onSuccess: () => onClose(),
  });

  // 내정보 수정 api - 전문가회원
  const editCompanyMe = useEditCompanyMe({
    onError: (message) => setErrorMessage(message),
    onSuccess: () => onClose(),
  });

  // 업종 목록 api
  const jobs = useJob();

  // 내정보 조회 api - 일반회원
  const userMe = useUserMe();

  // 내정보 조회 api  - 전문가회원
  const companyMe = useCompanyMe();

  const me = companyMe || userMe;

  // 내 정보 상태관리
  const [meState, dispatchMeState] = useInput({
    userName: me?.userName || "",
    password: me?.password || "",
    job: me?.job || "",
  });
  const [userMeState, dispatchUserMeState] = useInput({
    name: userMe?.name || "",
    phoneNumber: userMe?.phoneNumber || "",
    email: userMe?.email || "",
    companyName: userMe?.companyName || "",
  });
  const [companyMeState, dispatchCompanyMeState] = useInput({
    name: companyMe?.name || "",
    site: companyMe?.site || "",
    contactNumber: companyMe?.contactNumber || "",
    email: companyMe?.email || "",
    managerName: companyMe?.managerName || "",
    managerNumber: companyMe?.managerNumber || "",
    license: companyMe?.license || "",
  });
  const [addressState, dispatchAddressState] = useInput({
    postCode: "",
    baseAddress: "",
    detailAddress: "",
  });

  // 내 정보 조회 초기상태 업데이트
  const handleInitialState = () => {
    if (!me) return;

    const initialAddress = getParsedJSON<Address>(me.address);

    if (initialAddress) {
      dispatchAddressState({ objectState: initialAddress });
    }

    const isEtc = jobs?.find((j) => j.name === me.job);

    if (!isEtc) {
      setJobEtc(me.job);
    }
  };

  useEffect(() => {
    handleInitialState();
  }, [userMe, companyMe]);

  if (!me || !jobs) return null;

  // 내 정보 수정하기 이벤트
  const handleSave = (e: InputForm) => {
    e.preventDefault();

    const newAddress = JSON.stringify(addressState);
    const newJob =
      selectIndex === jobOptions.length - 1
        ? jobEtc
        : jobOptions[selectIndex].value;

    const isAddress = Object.values(addressState).every((v) => !v);

    if (me.type === "user") {
      const validation =
        !userMeState.name || !userMeState.phoneNumber || !userMeState.email;

      if (validation) {
        setErrorMessage("*표시는 필수 작성 항목입니다.");
        return;
      }

      const userDto = {
        ...meState,
        ...userMeState,
        address: newAddress,
        job: newJob,
      };

      editUserMe(userDto);
    } else {
      const validation =
        !companyMeState.name ||
        !companyMeState.contactNumber ||
        !companyMeState.email ||
        !companyMeState.managerName ||
        !companyMeState.managerNumber ||
        !companyMeState.license ||
        isAddress;

      if (validation) {
        setErrorMessage("*표시는 필수 작성 항목입니다.");
        return;
      }

      const companyDto = {
        ...meState,
        ...companyMeState,
        address: newAddress,
        job: newJob,
      };

      editCompanyMe(companyDto);
    }
  };

  // 업종 선택
  const jobOptions = jobs.map((job) => ({ name: job.name, value: job.name }));
  const jobIndex = jobOptions.findIndex((j) => j.value === meState.job);
  const initialJobIndex = jobIndex === -1 ? jobOptions.length - 1 : jobIndex;

  const titleWidth = me.type === "user" ? 80 : 80;

  return (
    <ModalStyle.Large className="w-[996px]">
      <form onSubmit={handleSave}>
        <H2 className="mb-10">마이페이지</H2>
        <Row className="mb-5 w-[50%] pr-3">
          <Title width={titleWidth}>직업 유형</Title>
          <Row gap={12}>
            <Select
              options={jobOptions || []}
              selectIndex={selectIndex}
              onChageSelectIndex={(i) => setSelectIndex(i)}
              shape="square"
              className="w-[130px]"
              initialIndex={initialJobIndex}
            />
            {selectIndex === jobOptions.length - 1 && (
              <InputText
                shape="square"
                name="jobEtc"
                value={jobEtc}
                onChange={(event) => setJobEtc(event.target.value)}
              />
            )}
          </Row>
        </Row>
        <Row gap={24} className="mb-5">
          <Row>
            <Title width={titleWidth}>아이디</Title>
            <InputText
              shape="square"
              name="userName"
              value={meState?.userName}
              readOnly
            />
          </Row>
          <Row>
            <Title width={titleWidth}>비밀번호</Title>
            <InputText
              shape="square"
              type="password"
              name="password"
              value={meState?.password}
              onChange={(event) => dispatchMeState({ event })}
            />
          </Row>
        </Row>

        {
          {
            user: (
              <FormUser
                userMeState={userMeState}
                onChage={(event) => dispatchUserMeState({ event })}
              />
            ),
            company: (
              <FormCompany
                companyMeState={companyMeState}
                onChageText={(event) => dispatchCompanyMeState({ event })}
                onChageLicense={(value) =>
                  dispatchCompanyMeState({ option: { key: "license", value } })
                }
              />
            ),
          }[me.type]
        }

        <div className="flex">
          <Row className="!items-start">
            <Title width={titleWidth} className="!leading-[44px]">
              주소{me.type === "company" && <Essential>*</Essential>}
            </Title>
            <FormAddress
              addressState={addressState}
              chageEvent={{
                onChageDetail: (value) =>
                  dispatchAddressState({
                    option: { key: "detailAddress", value },
                  }),
                onChageBase: (value) =>
                  dispatchAddressState({
                    option: { key: "baseAddress", value },
                  }),
                onChagePostCode: (value) =>
                  dispatchAddressState({ option: { key: "postCode", value } }),
              }}
              shape="square"
              className="gap-5 pr-6"
            />
          </Row>
          <div className="flex flex-col justify-end">
            <Modal
              activator={(onOpen) => (
                <BtnDelete type="button" onClick={onOpen}>
                  회원탈퇴
                </BtnDelete>
              )}
              modal={(onClose) => (
                <ModalDeleteMe
                  onClose={onClose}
                  type={me.type}
                  onError={(message) => setErrorMessage(message)}
                />
              )}
              backdropHidden
            />
          </div>
        </div>

        {errorMessage && (
          <Caption className="text-error-active mt-5">{errorMessage}</Caption>
        )}

        <div className="flex justify-between mt-10">
          <Btn.Gray onClick={onClose} size="large" width="100px" type="button">
            취소
          </Btn.Gray>
          <Btn.Blue size="large" width="100px" type="submit">
            저장하기
          </Btn.Blue>
        </div>
      </form>
    </ModalStyle.Large>
  );
};

const Row = styled.div<{ gap?: number }>`
  display: flex;
  gap: ${({ gap }) => gap || 32}px;
  align-items: center;
  flex: 1;
`;

const Title = styled(TextSm)<{ width: number }>`
  width: ${({ width }) => width}px;
  flex: none;
  font-weight: 700;
`;

const BtnDelete = styled.button`
  color: ${COLORS.black["40%"]};
  font-size: 14px;
  line-height: 44px;
  text-decoration-line: underline;
`;

const Essential = styled.span`
  vertical-align: top;
  color: ${COLORS.error.active};
  line-height: 40px;
`;

export default ModalMy;
